import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useCreateProjectNote, useProject, useProjectNotes, useWorkspace } from '@fluxter/shared';
import { Plus } from 'lucide-react';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import { NoteCard, type NoteRecord } from '@/components/NoteCard';
import { NoteRow } from '@/components/NoteRow';
import { NotesGroupHeader } from '@/components/NotesGroupHeader';
import { NotesSubBar, type NotesLayout } from '@/components/NotesSubBar';
import { NotePreviewPanel } from '@/components/NotePreviewPanel';
import { NewNoteCard } from '@/components/NewNoteCard';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { SkeletonList } from '@/components/Skeletons';
import { groupNotes, type GroupMode } from '@/lib/notes-grouping';
import { useLocalPref } from '@/lib/use-local-pref';
import { cn } from '@/lib/utils';

interface NoteAttrs {
  title?: string;
  body?: string;
  project_slug?: string | null;
  project_name?: string | null;
  task_id?: string | null;
  task_title?: string | null;
  created_by?: string | null;
  creator_name?: string | null;
  creator_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface NoteListItem {
  id: string;
  attributes?: NoteAttrs;
}

interface NoteListPayload {
  data?: NoteListItem[];
}

interface NoteCreateResponse {
  data?: { id?: string };
}

const isLayout = (raw: unknown): raw is NotesLayout =>
  raw === 'cards' || raw === 'rows' || raw === 'pin';
const isProjectGroupMode = (raw: unknown): raw is GroupMode =>
  raw === 'recency' || raw === 'author';

/**
 * Map an API note row → `NoteRecord`. Project page never sees task-
 * parented notes (the project notes endpoint scopes to `project_id`),
 * so the task branch is a defensive no-op.
 */
const toNoteRecord = (row: NoteListItem, projectSlug: string, projectName: string | undefined): NoteRecord | null => {
  const a = row.attributes ?? {};
  const updatedIso = a.updated_at ?? a.created_at ?? null;
  const updated = updatedIso ? new Date(updatedIso).getTime() : Date.now();
  const recencyMinutes = Math.max(0, (Date.now() - updated) / 60_000);
  const edited = Boolean(
    a.updated_at && a.created_at && a.updated_at !== a.created_at,
  );

  return {
    id: row.id,
    title: a.title ?? '',
    body: a.body ?? '',
    parent: {
      kind: 'project',
      projectSlug: a.project_slug ?? projectSlug,
      projectName: a.project_name ?? projectName ?? 'Untitled',
    },
    projectSlug: a.project_slug ?? projectSlug,
    projectName: a.project_name ?? projectName ?? 'Untitled',
    railSeed: a.project_slug ?? projectSlug,
    authorId: a.created_by ?? null,
    authorName: a.creator_name ?? null,
    authorEmail: a.creator_email ?? null,
    recencyMinutes,
    edited,
  };
};

export const ProjectNotesPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/notes',
  });
  const navigate = useNavigate();
  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const notes = useProjectNotes(workspaceSlug, projectSlug);
  const createNote = useCreateProjectNote(workspaceSlug, projectSlug);

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const projectName = (project.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const [search, setSearch] = useState('');
  const [openNote, setOpenNote] = useState<NoteRecord | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  // Per-page persisted layout + group-by. Project page omits the
  // `project` group option (Q4 trim — single-project context makes
  // it moot) and the parent-type tabs.
  const [layout, setLayout] = useLocalPref<NotesLayout>(
    `notes-layout:${workspaceSlug}:project:${projectSlug}`,
    'cards',
    isLayout,
  );
  const [groupMode, setGroupMode] = useLocalPref<GroupMode>(
    `notes-group:${workspaceSlug}:project:${projectSlug}`,
    'recency',
    isProjectGroupMode,
  );

  /* ─ Adapt + filter + sort + group ────────────────────────────── */
  const allRecords = useMemo<NoteRecord[]>(() => {
    const rows = (notes.data as NoteListPayload | undefined)?.data ?? [];
    return rows
      .map((r) => toNoteRecord(r, projectSlug, projectName))
      .filter((r): r is NoteRecord => r !== null);
  }, [notes.data, projectSlug, projectName]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allRecords;
    return allRecords.filter((n) => {
      const haystack = `${n.title} ${n.body} ${n.authorName ?? ''} ${n.authorEmail ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [allRecords, search]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => a.recencyMinutes - b.recencyMinutes),
    [filtered],
  );

  const groups = useMemo(() => groupNotes(sorted, groupMode), [sorted, groupMode]);

  /* ─ Create flow ──────────────────────────────────────────────── */

  /**
   * Doc-style create flow: POST an empty `Untitled` note, then
   * navigate straight to its detail page. The detail page auto-opens
   * edit mode for fresh `Untitled` notes so the user lands directly
   * on the writing surface.
   */
  const handleNewNote = async (): Promise<void> => {
    setCreateError(null);
    try {
      const result = (await createNote.mutateAsync({
        title: 'Untitled',
        body: '',
      })) as NoteCreateResponse;
      const newId = result?.data?.id;
      if (!newId) {
        setCreateError('Note saved but the response was malformed.');
        return;
      }
      await navigate({
        to: '/w/$workspaceSlug/n/$noteUlid',
        params: { workspaceSlug, noteUlid: newId },
      });
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Could not create note.');
    }
  };

  /* ─ Topbar slots ─────────────────────────────────────────────── */
  // ProjectPageHeader contributes the breadcrumb on the left slot;
  // ProjectViewSwitcher on the right slot; CTA = direct create.

  const topBarRight = useMemo(
    () => (
      <ProjectViewSwitcher
        workspaceSlug={workspaceSlug}
        projectSlug={projectSlug}
        current="notes"
      />
    ),
    [workspaceSlug, projectSlug],
  );

  const topBarCta = useMemo(
    () => (
      <button
        type="button"
        onClick={() => void handleNewNote()}
        disabled={createNote.isPending}
        className={cn(
          'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border-0 px-2.5 text-[12.5px] font-medium text-white transition-shadow duration-100',
          'bg-[var(--primary)]',
          'shadow-[0_2px_8px_oklch(0.3_0.06_245/0.3)] hover:shadow-[0_3px_10px_oklch(0.3_0.06_245/0.4)]',
          'dark:shadow-[0_2px_12px_oklch(0.55_0.12_245/0.4)]',
          'disabled:opacity-50',
        )}
      >
        <Plus className="size-3.5" aria-hidden="true" />
        {createNote.isPending ? 'Creating…' : 'New note'}
      </button>
    ),
    // handleNewNote re-binds per render via createNote; intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createNote.isPending],
  );

  useSetTopBarSlots({ right: topBarRight, cta: topBarCta });

  return (
    <div className="relative flex flex-col gap-4">
      <ProjectPageHeader
        workspaceSlug={workspaceSlug}
        workspaceName={workspaceName}
        projectSlug={projectSlug}
        projectName={projectName}
        current="notes"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 blur-[20px]"
        style={{
          background:
            'radial-gradient(50% 40% at 90% 0%, var(--brand-glow-1), transparent 65%),' +
            'radial-gradient(40% 50% at 5% 20%, var(--brand-glow-2), transparent 60%)',
        }}
      />

      <header className="relative z-[1]">
        <h1 className="text-foreground text-[26px] font-semibold tracking-[-0.025em]">
          Notes
        </h1>
        <p className="text-foreground-faint mt-1 max-w-[720px] text-[12.5px] leading-[1.5]">
          Markdown documents pinned to this project — meeting notes, decisions, briefs.
          Notes attached to specific tasks live on each task's detail page.
        </p>
      </header>

      {createError ? (
        <p className="text-destructive relative z-[2] text-[12.5px]" role="alert">
          {createError}
        </p>
      ) : null}

      <div className="relative z-[2]">
        <NotesSubBar
          // No filter tabs on the project page — every note here is
          // already scoped to this project (Q4 trim).
          tabs={undefined}
          groupMode={groupMode}
          // Project group hidden on this page; only recency + author.
          groupOptions={['recency', 'author']}
          onGroupChange={setGroupMode}
          layout={layout}
          onLayoutChange={setLayout}
          search={search}
          onSearchChange={setSearch}
        />
      </div>

      <div className="relative z-[1]">
        {notes.isLoading ? (
          <SkeletonList rows={4} withAvatar />
        ) : allRecords.length === 0 ? (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-20 text-center backdrop-blur-md',
              'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
            )}
          >
            <span
              className={cn(
                'inline-flex size-12 items-center justify-center rounded-xl border',
                'border-border bg-secondary text-foreground-faint',
              )}
            >
              <Plus className="size-5" aria-hidden="true" />
            </span>
            <p className="text-foreground m-0 text-[14px] font-medium">
              No notes yet for this project.
            </p>
            <p className="text-foreground-faint m-0 max-w-[320px] text-[12px]">
              Hit <span className="text-foreground font-medium">New note</span> to
              start a project log.
            </p>
          </div>
        ) : sorted.length === 0 ? (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-20 text-center backdrop-blur-md',
              'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
            )}
          >
            <p className="text-foreground m-0 text-[14px] font-medium">
              No notes match the current filter.
            </p>
            <p className="text-foreground-faint m-0 text-[12px]">
              Try clearing the search.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-[22px]">
            {groups.map((group) => (
              <section key={group.key}>
                <NotesGroupHeader group={group} />
                <NotesLayoutWrapper layout={layout}>
                  {group.items.map((note) =>
                    layout === 'rows' ? (
                      <NoteRow
                        key={note.id}
                        note={note}
                        search={search}
                        onOpen={setOpenNote}
                      />
                    ) : (
                      <NoteCard
                        key={note.id}
                        note={note}
                        search={search}
                        onOpen={setOpenNote}
                      />
                    ),
                  )}
                  {layout === 'cards' && group === groups[groups.length - 1] ? (
                    <NewNoteCard
                      onClick={() => void handleNewNote()}
                      disabled={createNote.isPending}
                    />
                  ) : null}
                </NotesLayoutWrapper>
              </section>
            ))}
          </div>
        )}
      </div>

      <NotePreviewPanel
        workspaceSlug={workspaceSlug}
        note={openNote}
        onClose={() => setOpenNote(null)}
      />
    </div>
  );
};

const NotesLayoutWrapper = ({
  layout,
  children,
}: {
  layout: NotesLayout;
  children: React.ReactNode;
}) => {
  if (layout === 'rows') {
    return (
      <div
        className={cn(
          'flex flex-col overflow-hidden rounded-xl border backdrop-blur-md',
          'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
        )}
      >
        {children}
      </div>
    );
  }
  if (layout === 'pin') {
    return (
      <div className="[column-gap:12px] [columns:3_280px] [&>*]:mb-3 [&>*]:[break-inside:avoid]">
        {children}
      </div>
    );
  }
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
      {children}
    </div>
  );
};
